import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  onSnapshot,
  WhereFilterOp,
  DocumentSnapshot,
  Unsubscribe,
  FirestoreDataConverter
} from 'firebase/firestore';
import type { Task } from '@segmently/schema-models/entities';
import { CoreFirebaseService } from './CoreFirebaseService';

/**
 * TaskService
 *
 * Type-safe service for Task entity CRUD operations (subcollection of Project).
 *
 * Features:
 * - Full CRUD operations for tasks within a project
 * - Query operations with pagination
 * - Real-time subscriptions with automatic cleanup
 * - Type-safe: only Task types exposed, no Firebase types in public API
 * - Scoped to parent project via constructor parameter
 *
 * Usage:
 * ```typescript
 * import { TaskService } from '@/modules/firebase-core/services/TaskService';
 * import { taskConverter } from '@schema/mappers/taskConverter';
 *
 * const service = new TaskService('project-123', taskConverter);
 *
 * // Create
 * const task = await service.create({ name: 'My Task' });
 *
 * // List with pagination
 * const tasks = await service.list({ limit: 20 });
 * ```
 *
 * Note: Full implementation in Sprint 5 (T012)
 */
export class TaskService {
  private readonly collectionName = 'tasks';
  private readonly projectId: string;
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<Task>;

  /**
   * Create a new TaskService instance for a specific project
   *
   * @param projectId - Parent project ID
   * @param converter - Firestore converter for Task entity (from schema-management)
   */
  constructor(projectId: string, converter: FirestoreDataConverter<Task>) {
    this.projectId = projectId;
    this.converter = converter;
  }

  /**
   * Get the subcollection path for tasks
   */
  private getCollectionPath(): string {
    return `projects/${this.projectId}/${this.collectionName}`;
  }

  /**
   * Create a new task
   */
  async create(data: Partial<Task>): Promise<Task> {
    try {
      const collectionRef = collection(this.firestore, this.getCollectionPath());
      const docRef = doc(collectionRef).withConverter(this.converter);

      const taskData: Task = {
        ...data,
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as Task;

      await setDoc(docRef, taskData);

      return taskData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'TaskService.create');
      throw error;
    }
  }

  /**
   * Get a task by ID
   */
  async get(id: string): Promise<Task | null> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id).withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as Task;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'TaskService.get');
      throw error;
    }
  }

  /**
   * Update an existing task
   *
   * Uses setDoc with merge instead of updateDoc to ensure converter is applied correctly.
   * This is the recommended pattern for client SDK with custom converters.
   */
  async update(id: string, data: Partial<Task>): Promise<Task> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id).withConverter(this.converter);

      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      // Use setDoc with merge: true instead of updateDoc
      // This ensures the converter's toFirestore is called correctly
      await setDoc(docRef, updateData as Task, { merge: true });

      const updated = await this.get(id);
      if (!updated) {
        throw new Error(`Task ${id} not found after update`);
      }

      return updated;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'TaskService.update');
      throw error;
    }
  }

  /**
   * Delete a task
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'TaskService.delete');
      throw error;
    }
  }

  /**
   * List tasks with optional pagination
   */
  async list(options: { limit?: number; startAfter?: any } = {}): Promise<Task[]> {
    try {
      const collectionRef = collection(this.firestore, this.getCollectionPath()).withConverter(this.converter);

      let q = query(collectionRef, orderBy('createdAt', 'desc'));

      if (options.limit) {
        q = query(q, limit(options.limit));
      }

      if (options.startAfter) {
        q = query(q, startAfter(options.startAfter));
      }

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Task));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'TaskService.list');
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for a task
   */
  onSnapshot(
    id: string,
    onNext: (task: Task | null) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id).withConverter(this.converter);

      return onSnapshot(
        docRef,
        (snapshot: DocumentSnapshot<Task>) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            onNext({ ...data, id: snapshot.id } as Task);
          } else {
            onNext(null);
          }
        },
        (error) => {
          CoreFirebaseService.getInstance().logError(error, 'TaskService.onSnapshot');
          onError(error as Error);
        }
      );
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'TaskService.onSnapshot.setup');
      throw error;
    }
  }
}
